import { Chip, Typography, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import {
  MOMENT_FORMATS,
  PAYMENT_METHOD_KV_MAP,
} from 'src/consumer/constants'
import { isValidNumber } from 'src/consumer/helpers/utilFunctions'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'

function FeeItem({ label, value, colorToken = 'text.secondary', fontWeight = 500 }) {
  const theme = useTheme()
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(0.5),
        flexWrap: 'wrap',
      }}
    >
      <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}>
        {label}:
      </Typography>
      <Typography component="span" variant="body2" color={colorToken} fontWeight={fontWeight}>
        {value}
      </Typography>
    </div>
  )
}

export default function PayoutDistributionReportFeeDetails({
  report,
  getClinicShare,
  getClinicSharePercentage,
  hideFeeDataOnZeroPercentage = false,
  hideClinicShare = false,
  hideAumhumShare = false,
  getCurrency,
  forMentor = false,
  getMinPayableValue,
}) {
  const isClinicOnly = report?.affiliatedToAUMHUM === false
  const clinicShare = getClinicShare(report)
  const clinicSharePercentage = getClinicSharePercentage(report)
  const currency = getCurrency(report)
  const minPayableValue = getMinPayableValue(report)
  let feePaid = report?.feeCollected
  let feePaidForCalculations = report?.feeCollected
  const payingToAUMHUM = minPayableValue > feePaid
  const paymentMethod = report?.paymentMethod

  const isUsingMinPayableValue =
    isValidNumber(minPayableValue) && minPayableValue > 0 && minPayableValue !== feePaid

  if (isUsingMinPayableValue) {
    feePaidForCalculations = minPayableValue
  }

  let aumhumSharePct = isValidNumber(clinicSharePercentage)
    ? `${100 - clinicSharePercentage}%`
    : '—'
  if (isUsingMinPayableValue) {
    aumhumSharePct += ` of ${getPriceStringWithCurrency(minPayableValue, report?.currency)} (min)`
  }
  let shareIsZero = isValidNumber(clinicSharePercentage) && clinicSharePercentage === 0

  if (shareIsZero && hideFeeDataOnZeroPercentage) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const chargedStr = isValidNumber(report?.feeCollectedFromPatient)
    ? getPriceStringWithCurrency(report.feeCollectedFromPatient, currency, true)
    : '—'
  const collectedStr = isValidNumber(feePaid)
    ? getPriceStringWithCurrency(feePaid, currency, true)
    : '—'
  const clinicShareStr = isValidNumber(clinicShare)
    ? getPriceStringWithCurrency(clinicShare, currency, true)
    : '—'
  const aumhumShareAmt = forMentor
    ? (feePaid || 0) - clinicShare
    : (feePaidForCalculations || 0) - clinicShare
  const aumhumShareStr = isValidNumber(aumhumShareAmt)
    ? getPriceStringWithCurrency(aumhumShareAmt, currency, true)
    : '—'
  const collectedAtStr = report?.collectedAt
    ? dayjs.utc(report.collectedAt).local().format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
    : '—'

  const theme = useTheme()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
      }}
    >
      {isClinicOnly && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Excluded from payout calculations"
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: theme.spacing(0.75),
        }}
      >
        <FeeItem label="Charged" value={chargedStr} colorToken="text.primary" fontWeight={600} />
        <Typography component="span" variant="body2" color="text.disabled" sx={{ mx: 0.25 }}>
          ·
        </Typography>
        <FeeItem label="Collected" value={collectedStr} colorToken="success.main" fontWeight={600} />
        <SessionModeChip
          paymentMethod={paymentMethod}
          forMentor={forMentor}
          hasClinicBrand={!!report?.clinicBrandId}
        />
      </div>

      {(!hideClinicShare || !hideAumhumShare) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.25),
          }}
        >
          {!hideClinicShare && (
            <FeeItem
              label={forMentor ? 'My share' : 'Clinic share'}
              value={
                isClinicOnly
                  ? 'Excluded from payout calculations'
                  : payingToAUMHUM
                    ? `${clinicShareStr} (after AUMHUM)`
                    : isValidNumber(clinicSharePercentage)
                      ? `${clinicShareStr} (${clinicSharePercentage}%)`
                      : clinicShareStr
              }
              colorToken="info.main"
              fontWeight={600}
            />
          )}
          {!hideAumhumShare && (
            <FeeItem
              label="AUMHUM share"
              value={
                isClinicOnly
                  ? 'Excluded from payout calculations'
                  : `${aumhumShareStr} (${aumhumSharePct})`
              }
              colorToken={isClinicOnly ? 'text.secondary' : 'primary.main'}
              fontWeight={600}
            />
          )}
        </div>
      )}

      <Typography variant="caption" color="text.secondary">
        Collected at {collectedAtStr}
      </Typography>
    </div>
  )
}

function SessionModeChip({ paymentMethod, forMentor = false, hasClinicBrand }) {
  const isAumhum = paymentMethod === PAYMENT_METHOD_KV_MAP.BOOKING_FEE_PAYMENT_METHOD_COLLECTED_BY_AUMHUM
  const label = isAumhum ? 'AUMHUM' : 'Clinic'
  const bg = isAumhum ? '#7458b8' : '#2563eb'

  if (forMentor && !isAumhum && !hasClinicBrand) return null

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#fff',
        bgcolor: bg,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  )
}
